import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUp, Sparkles, X } from 'lucide-react';
import LandingOptionalImage from '../LandingOptionalImage';
import { lpFile } from '../../../utils/landingPageAssets';
import { getAppDomainUrl } from '../../../utils/constants';
import { lockBodyScroll, unlockBodyScroll } from '../../../utils/bodyScrollLock';

/**
 * Раскрытое окно AI-чата лендинга: desktop ~90vh по центру, mobile fullscreen.
 * Состояние чата приходит из useLandingAgentChat (владелец — LandingAgentLauncher).
 */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const DESKTOP_MQ = '(min-width: 993px)';

/** Потолок авто-роста поля ввода: одна строка 48px, дальше растём до четырёх. */
const INPUT_MAX_HEIGHT = 116;

export default function LandingAgentChatOverlay({ open, onClose, chat, initialPrompt }) {
  const {
    ready,
    enabled,
    suggestions,
    onboardingText,
    messages,
    sending,
    captchaPending,
    limitReached,
    maxInputLength,
    sendMessage,
    ensureBootstrap,
  } = chat;

  const [input, setInput] = useState('');
  const [isDesktop, setIsDesktop] = useState(false);
  const dialogRef = useRef(null);
  const textareaRef = useRef(null);
  const scrollRef = useRef(null);
  const restoreFocusRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(DESKTOP_MQ);
    const sync = () => setIsDesktop(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    ensureBootstrap().catch(() => undefined);
    lockBodyScroll();
    document.body.classList.add('mb-agent-open');
    restoreFocusRef.current = document.activeElement;
    return () => {
      document.body.classList.remove('mb-agent-open');
      unlockBodyScroll();
      const node = restoreFocusRef.current;
      restoreFocusRef.current = null;
      if (node && typeof node.focus === 'function' && document.contains(node)) node.focus();
    };
  }, [open, ensureBootstrap]);

  useEffect(() => {
    if (!open) return;
    setInput(typeof initialPrompt === 'string' ? initialPrompt : '');
  }, [open, initialPrompt]);

  useLayoutEffect(() => {
    if (!open) return;
    const node = textareaRef.current;
    if (!node) {
      dialogRef.current?.focus?.();
      return;
    }
    node.focus();
    const end = node.value.length;
    try {
      node.setSelectionRange(end, end);
    } catch (_) {
      /* поле может не поддерживать выделение — не критично */
    }
  }, [open, limitReached, enabled]);

  useEffect(() => {
    const node = textareaRef.current;
    if (!open || !node) return;
    node.style.height = 'auto';
    node.style.height = `${Math.min(node.scrollHeight, INPUT_MAX_HEIGHT)}px`;
  }, [input, open, enabled, limitReached]);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node || !open) return;
    const nearBottom = node.scrollHeight - node.scrollTop - node.clientHeight < 96;
    if (nearBottom) node.scrollTop = node.scrollHeight;
  }, [messages, open, limitReached, sending]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (node) => node.offsetParent !== null || node === document.activeElement
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  const submit = useCallback(
    async (text) => {
      const value = String(text || '').trim();
      if (!value || sending || limitReached) return;
      setInput(value);
      const answered = await sendMessage(value);
      if (answered) setInput((current) => current === value ? '' : current);
    },
    [sendMessage, sending, limitReached]
  );

  if (!open) return null;

  const canSend = enabled && !limitReached && !sending && input.trim().length > 0;
  const showSuggestions = enabled && !limitReached && suggestions.length > 0 && messages.length === 0;
  const registerUrl = getAppDomainUrl('/register');

  return createPortal(
    <div className="mb-agent-overlay" role="presentation" onKeyDown={handleKeyDown}>
      <button
        type="button"
        className="mb-agent-overlay__backdrop"
        aria-label="Закрыть чат"
        onClick={onClose}
      />
      <div
        className="mb-agent-chat"
        role="dialog"
        aria-modal="true"
        aria-label="Чат с AI-помощником Markbase"
        aria-labelledby="mb-agent-chat-title"
        ref={dialogRef}
        tabIndex={-1}
      >
        {isDesktop ? (
          <div className="mb-agent-chat__bg" aria-hidden>
            <LandingOptionalImage
              src={lpFile('b13_chat_open_bg.jpg')}
              alt=""
              className="mb-agent-chat__bg-img"
            />
          </div>
        ) : null}

        <header className="mb-agent-chat__head">
          <span className="mb-agent-chat__mark" aria-hidden>
            <Sparkles size={16} />
          </span>
          <div className="mb-agent-chat__titles">
            <h2 className="mb-agent-chat__title" id="mb-agent-chat-title">
              AI Markbase
            </h2>
            <p className="mb-agent-chat__subtitle">Отвечает встроенный помощник платформы</p>
          </div>
          <button
            type="button"
            className="mb-agent-chat__close"
            onClick={onClose}
            aria-label="Закрыть чат"
          >
            <X size={20} aria-hidden />
          </button>
        </header>

        <div className="mb-agent-chat__body" ref={scrollRef} aria-live="polite" aria-relevant="additions">
          {onboardingText ? (
            <div className="mb-agent-chat__intro">
              <p>{onboardingText}</p>
            </div>
          ) : null}

          {!ready ? (
            <div className="mb-agent-chat__notice mb-agent-chat__notice--loading">
              <p>Загружаю помощника…</p>
            </div>
          ) : null}

          {ready && !enabled ? (
            <div className="mb-agent-chat__notice">
              <p className="mb-agent-chat__notice-title">Чат сейчас недоступен</p>
              <p>
                Помощник временно не отвечает. Задачу можно решить в кабинете — бесплатный тариф
                открыт сразу после регистрации. Или напишите нам, и ответит человек.
              </p>
              <div className="mb-agent-chat__notice-actions">
                <a className="mb-agent-chat__notice-cta" href={registerUrl}>
                  Начать бесплатно
                </a>
                <a className="mb-agent-chat__notice-link" href="/contact">
                  Написать на почту
                </a>
              </div>
            </div>
          ) : null}

          {messages.map((message) => (
            <div
              key={message.id}
              className={[
                'mb-agent-msg',
                message.role === 'user' ? 'mb-agent-msg--user' : 'mb-agent-msg--assistant',
                message.failed ? 'mb-agent-msg--failed' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {message.pending && captchaPending ? (
                <p className="mb-agent-msg__text">Подтвердите проверку безопасности…</p>
              ) : message.pending ? (
                <span className="mb-agent-msg__typing" aria-label="Собираю ответ…">
                  <span />
                  <span />
                  <span />
                </span>
              ) : (
                <p className="mb-agent-msg__text">{message.content}</p>
              )}
            </div>
          ))}

          {showSuggestions ? (
            <div className="mb-agent-chat__suggestions">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  type="button"
                  className="mb-agent-chat__suggestion"
                  onClick={() => submit(suggestion.prompt)}
                  disabled={sending}
                >
                  {suggestion.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <footer className="mb-agent-chat__foot">
          {limitReached ? (
            <div className="mb-agent-chat__limit">
              <p className="mb-agent-chat__limit-title">Дальше — в вашем кабинете</p>
              <p className="mb-agent-chat__limit-text">
                Здесь можно задать несколько вопросов без регистрации. Чтобы AI работал с вашими
                товарами и файлами, откройте бесплатный аккаунт.
              </p>
              <a className="mb-agent-chat__limit-cta" href={registerUrl}>
                Создать бесплатный аккаунт
              </a>
              <button type="button" className="mb-agent-chat__limit-secondary" onClick={onClose}>
                Продолжить читать сайт
              </button>
            </div>
          ) : enabled ? (
            <form
              className="mb-agent-chat__form"
              onSubmit={(event) => {
                event.preventDefault();
                submit(input);
              }}
            >
              <label className="mb-agent-chat__field">
                <span className="mb-agent-chat__field-label">Ваш вопрос</span>
                <textarea
                  ref={textareaRef}
                  className="mb-agent-chat__input"
                  value={input}
                  onChange={(event) => setInput(event.target.value.slice(0, maxInputLength))}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      submit(input);
                    }
                  }}
                  rows={1}
                  placeholder="Опишите задачу своими словами"
                  aria-label="Опишите задачу"
                  aria-describedby="mb-agent-chat-hint"
                  maxLength={maxInputLength}
                />
              </label>
              <button
                type="submit"
                className="mb-agent-chat__send"
                disabled={!canSend}
                aria-label="Отправить вопрос"
              >
                <ArrowUp size={18} aria-hidden />
              </button>
            </form>
          ) : null}

          <p className="mb-agent-chat__privacy" id="mb-agent-chat-hint">
            {enabled && !limitReached ? (
              <span className="mb-agent-chat__privacy-keys">
                Enter — отправить, Shift + Enter — новая строка.
              </span>
            ) : null}{' '}
            Не вводите пароли и личные данные — это публичный чат.
          </p>
        </footer>
      </div>
    </div>,
    document.body
  );
}
